import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import useGetRTMTransferOptions, {
    HardshipOffer,
    PlanOffer
} from '../../../../Membership/api/useGetRTMTransferOptions';
import { PlanOffersSchemaParams } from '../../../../../views/CancellationFlow/validator/planOffersSchema';
import { MitigationOffersSchemaParams } from '../../../../../views/CancellationFlow/validator/mitigationOffersSchema';
import { HardshipOffersSchemaParams } from '../../../../../views/CancellationFlow/validator/hardshipOffers';

type Step =
    'mitigation-offers' | 'plan-offers' | 'hardship-offers' | 'collect-payment-method' | 'success';

interface MitigationData {
    months: number;
    discount_type: number;
    mitigation_type: number;
    hardship: string;
}

interface TransferToD2CContextValue {
    step: number;
    state: Step;
    heading: string;
    heroUserId: number;
    rtmPlanOffers: PlanOffer[] | undefined;
    rtmHardshipOffers: HardshipOffer[] | undefined;
    isLoadingPlans: boolean;
    selectedPlan: string | undefined;
    selectedMitigation: MitigationData | undefined;
    handleChooseMitigationOffer: (values: MitigationOffersSchemaParams) => void;
    handleChoosePlan: (values: PlanOffersSchemaParams) => void;
    handleChooseHardship: (values: HardshipOffersSchemaParams) => void;
    onCreditCardSuccess: () => void;
    goBackToMitigationOffers: () => void;
}

const TransferToD2CContext = createContext<TransferToD2CContextValue | null>(null);

interface ProviderProps {
    heroUserId: number;
    children: React.ReactNode;
}

export const TransferToD2CProvider: React.FC<ProviderProps> = ({ heroUserId, children }) => {
    const [state, setState] = useState<Step>('mitigation-offers');
    const [selectedPlan, setSelectedPlan] = useState<string | undefined>();
    const [selectedMitigation, setSelectedMitigation] = useState<MitigationData | undefined>();

    const { data, isLoading: isLoadingPlans } = useGetRTMTransferOptions({
        hero_user_id: heroUserId,
        enabled: true
    });

    const rtmPlanOffers = data?.data?.data?.plans;
    const rtmHardshipOffers = data?.data?.data?.offers;

    const handleChooseMitigationOffer = useCallback((values: MitigationOffersSchemaParams) => {
        if (values.mitigation_offers === 'regular-plans') {
            setState('plan-offers');
        } else {
            setState('hardship-offers');
        }
    }, []);

    const handleChoosePlan = useCallback((values: PlanOffersSchemaParams) => {
        setSelectedPlan(values.plan_offers);
        setSelectedMitigation(undefined);
        setState('collect-payment-method');
    }, []);

    const handleChooseHardship = useCallback((values: HardshipOffersSchemaParams) => {
        setSelectedMitigation({
            months: values.months,
            discount_type: values.discount_type,
            mitigation_type: values.mitigation_type,
            hardship: values.hardship
        });
        setSelectedPlan(undefined);
        setState('collect-payment-method');
    }, []);

    const onCreditCardSuccess = useCallback(() => {
        setState('success');
    }, []);

    const goBackToMitigationOffers = useCallback(() => {
        setState('mitigation-offers');
    }, []);

    const step = useMemo(() => {
        if (state === 'mitigation-offers') return 1;
        if (state === 'plan-offers' || state === 'hardship-offers') return 2;
        if (state === 'collect-payment-method') return 3;
        return 3;
    }, [state]);

    const heading = useMemo(() => {
        if (state === 'mitigation-offers') return 'Transfer to D2C';
        if (state === 'plan-offers') return 'Select a Plan';
        if (state === 'hardship-offers') return 'Hardship Offers';
        if (state === 'collect-payment-method') return 'Collect Payment Method';
        return 'Transfer Complete';
    }, [state]);

    const value = useMemo(
        () => ({
            step,
            state,
            heading,
            heroUserId,
            rtmPlanOffers,
            rtmHardshipOffers,
            isLoadingPlans,
            selectedPlan,
            selectedMitigation,
            handleChooseMitigationOffer,
            handleChoosePlan,
            handleChooseHardship,
            onCreditCardSuccess,
            goBackToMitigationOffers
        }),
        [
            step,
            state,
            heading,
            heroUserId,
            rtmPlanOffers,
            rtmHardshipOffers,
            isLoadingPlans,
            selectedPlan,
            selectedMitigation,
            handleChooseMitigationOffer,
            handleChoosePlan,
            handleChooseHardship,
            onCreditCardSuccess,
            goBackToMitigationOffers
        ]
    );

    return <TransferToD2CContext.Provider value={value}>{children}</TransferToD2CContext.Provider>;
};

export const useTransferToD2C = () => {
    const context = useContext(TransferToD2CContext);
    if (!context) {
        throw new Error('useTransferToD2C must be used within a TransferToD2CProvider');
    }
    return context;
};
